"use client";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ToggleGroup,
  ToggleGroupItem,
} from "@pex/ui-web";
import { ArrowUpRight, ChevronDown, Search, X } from "lucide-react";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { SearchDropdown } from "@/components/in-person/search-dropdown";
import { LOCATION_ALIASES } from "@/lib/location-aliases";
import type { DiscoveryItem, FilterOptions } from "@/lib/queries/discovery";
import type {
  ItemSuggestion,
  PlaceSuggestion,
  SearchApiResult,
  SearchSuggestion,
} from "@/lib/search";

export interface UrlFilters {
  group_type: string;
  meeting_day: string;
  programme_type: string;
  type: "all" | "group" | "programme";
}

export interface ProgrammeTypeOption {
  code: string;
  description: string | null;
  logoUrl: string | null;
  name: string;
}

interface Props {
  filteredItems: DiscoveryItem[];
  filterOptions: FilterOptions;
  filters: UrlFilters;
  /** Returns the current map center [lng, lat] for Mapbox proximity bias. */
  getMapCenter: () => [number, number] | undefined;
  groupTypeOptions: { label: string; value: string }[];
  onFiltersChange: (updates: Partial<UrlFilters>) => void;
  onSearchChange: (value: string) => void;
  onSuggestionSelect: (suggestion: SearchSuggestion) => void;
  programmeTypes: ProgrammeTypeOption[];
  searchText: string;
}

const PILL =
  "rounded-full! px-3 text-sm shadow-sm bg-background! data-[state=on]:bg-[var(--brand-carrara-light)]! data-[state=on]:text-foreground! data-[state=on]:border-[var(--brand-carrara-darker)]!";

// Standalone pill for non-ToggleGroup buttons (no toggleVariants base to inherit from)
const DROPDOWN_PILL =
  "inline-flex h-9 items-center justify-center gap-1 whitespace-nowrap rounded-full border border-input bg-background px-3 font-medium text-sm shadow-sm transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none data-[state=on]:bg-[var(--brand-carrara-light)] data-[state=on]:border-[var(--brand-carrara-darker)] data-[state=on]:text-foreground";

function ChairIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="currentColor"
      viewBox="0 -960 960 960"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M200-120v-308q0-26 17-43t43-17h60v-90h-60q-26 0-43-17t-17-43v-142q0-26 17-43t43-17h440q26 0 43 17t17 43v142q0 26-17 43t-43 17h-60v90h60q26 0 43 17t17 43v308h-60v-122H260v122h-60Zm180-368h200v-90H380v90Z" />
    </svg>
  );
}

// Short taglines shown in the dropdown. The DB has a full `description` field on
// programme_definitions — a `short_description` column should be added there and
// used here instead (deferred — see session notes).
const PROGRAMME_TAGLINES: Record<string, string> = {
  ers: "Addiction recovery foundation",
  resound: "Support for families and loved ones",
  thrive: "Personal growth and emotional wellness",
  vantage: "Freedom from pornography addiction",
};

// Countries where PEx groups are present — restricts Mapbox geocoding results.
const MAPBOX_COUNTRIES =
  "za,zw,mw,mz,zm,bw,na,ls,sz,ke,ng,gh,ug,tz,rw,cd,ao,et,cm,sn,ci";

export function FilterBar({
  filterOptions,
  filteredItems,
  filters,
  getMapCenter,
  groupTypeOptions,
  onFiltersChange,
  onSearchChange,
  onSuggestionSelect,
  programmeTypes,
  searchText,
}: Props) {
  const typeActive = filters.type !== "all";

  let typeTriggerLabel = "Type";
  if (filters.type === "group") {
    typeTriggerLabel =
      groupTypeOptions.find((o) => o.value === filters.group_type)?.label ??
      "All groups";
  } else if (filters.type === "programme") {
    typeTriggerLabel =
      programmeTypes.find((p) => p.code === filters.programme_type)?.name ??
      "All programmes";
  }

  // --- Search dropdown state ---

  // Debounce only the API calls (150ms). Client-side matches show immediately.
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    if (!searchText || searchText.length < 2) {
      setDebouncedQuery("");
      return;
    }
    const id = setTimeout(() => setDebouncedQuery(searchText), 300);
    return () => clearTimeout(id);
  }, [searchText]);

  // API results (Mapbox places + /api/search items)
  const [apiPlaces, setApiPlaces] = useState<PlaceSuggestion[]>([]);
  const [apiItems, setApiItems] = useState<ItemSuggestion[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Immediate client-side item matches — shown instantly as the user types,
  // no debounce, no API wait. API results supplement/replace these when ready.
  const immediateClientItems = useMemo((): ItemSuggestion[] => {
    if (searchText.length < 2) {
      return [];
    }
    const q = searchText.toLowerCase();
    return filteredItems
      .filter((item) => {
        if (item.data.latitude == null || item.data.longitude == null) {
          return false;
        }
        const name =
          item.kind === "group"
            ? item.data.name
            : (item.data.definition?.name ?? "");
        return name.toLowerCase().includes(q);
      })
      .slice(0, 5)
      .map(
        (item): ItemSuggestion => ({
          kind: item.kind,
          id: item.data.id,
          name:
            item.kind === "group"
              ? item.data.name
              : (item.data.definition?.name ?? ""),
          city: item.data.city,
          coordinates: [
            item.data.longitude as number,
            item.data.latitude as number,
          ],
          logoUrl:
            item.kind === "programme"
              ? (item.data.definition?.logo_letter_url ?? null)
              : null,
        })
      );
  }, [searchText, filteredItems]);

  // Geographically nearest items to the top place suggestion — shown when the
  // query resolves to a location (city, province, country) rather than a name.
  // Covers the case where groups have no province column so text-search returns
  // nothing but the user still wants to see what's in e.g. "KwaZulu-Natal".
  const nearbyItems = useMemo((): ItemSuggestion[] => {
    const topPlace = apiPlaces[0];
    if (!topPlace) {
      return [];
    }
    const [lng, lat] = topPlace.coordinates;
    const existingIds = new Set([
      ...apiItems.map((i) => i.id),
      ...immediateClientItems.map((i) => i.id),
    ]);
    // ~5 degrees ≈ 550 km — only surface items that are plausibly in the region
    const MAX_DIST_SQ = 5 * 5;
    return filteredItems
      .filter(
        (item) =>
          item.data.latitude != null &&
          item.data.longitude != null &&
          !existingIds.has(item.data.id)
      )
      .map((item) => ({
        item,
        distSq:
          ((item.data.longitude ?? 0) - lng) ** 2 +
          ((item.data.latitude ?? 0) - lat) ** 2,
      }))
      .filter(({ distSq }) => distSq <= MAX_DIST_SQ)
      .sort((a, b) => a.distSq - b.distSq)
      .slice(0, 3)
      .map(
        ({ item }): ItemSuggestion => ({
          kind: item.kind,
          id: item.data.id,
          name:
            item.kind === "group"
              ? item.data.name
              : (item.data.definition?.name ?? ""),
          city: item.data.city,
          coordinates: [
            item.data.longitude as number,
            item.data.latitude as number,
          ],
          logoUrl:
            item.kind === "programme"
              ? (item.data.definition?.logo_letter_url ?? null)
              : null,
        })
      );
  }, [apiPlaces, filteredItems, apiItems, immediateClientItems]);

  // Displayed items = API results + immediate client matches + nearby-to-place items
  const displayedItems = useMemo((): ItemSuggestion[] => {
    const apiIds = new Set(apiItems.map((i) => i.id));
    const nearbyIds = new Set(nearbyItems.map((i) => i.id));
    const clientOnly = immediateClientItems.filter(
      (i) => !(apiIds.has(i.id) || nearbyIds.has(i.id))
    );
    return [...apiItems, ...clientOnly, ...nearbyItems].slice(0, 8);
  }, [apiItems, immediateClientItems, nearbyItems]);

  // Show spinner only when loading AND there's nothing to display yet
  const showLoading =
    loading && displayedItems.length === 0 && apiPlaces.length === 0;
  // Show "no results" only after load completes with nothing to show
  const showEmpty =
    !loading &&
    debouncedQuery.length >= 2 &&
    apiPlaces.length === 0 &&
    displayedItems.length === 0;

  const dropdownOpen =
    searchText.length >= 2 &&
    (showLoading ||
      apiPlaces.length > 0 ||
      displayedItems.length > 0 ||
      showEmpty);
  const totalOptions = apiPlaces.length + displayedItems.length;

  // Cancel any in-flight requests when a new query arrives
  const abortControllerRef = useRef<AbortController | null>(null);

  // Parallel fetch: Mapbox places + internal groups/programmes
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setApiPlaces([]);
      setApiItems([]);
      setLoading(false);
      return;
    }

    // Abort the previous request pair and clear stale results
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    setApiPlaces([]);
    setApiItems([]);
    setLoading(true);

    const effectiveQuery =
      LOCATION_ALIASES[debouncedQuery.toLowerCase()] ?? debouncedQuery;

    const mapboxToken = process.env.NEXT_PUBLIC_MAPBOX_TOKEN ?? "";
    const center = getMapCenter();
    const proximityParam = center ? `&proximity=${center[0]},${center[1]}` : "";

    const placesPromise = fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(effectiveQuery)}.json?access_token=${mapboxToken}&types=country,region,district,place,locality,neighborhood,postcode&limit=3&language=en&country=${MAPBOX_COUNTRIES}${proximityParam}`,
      { signal }
    )
      .then((r) => r.json())
      .then((json) => {
        interface MapboxFeature {
          bbox?: number[];
          geometry: { coordinates: [number, number] };
          id: string;
          place_name: string;
        }
        return (json.features ?? []).map(
          (f: MapboxFeature): PlaceSuggestion => ({
            kind: "place",
            id: f.id,
            displayName: f.place_name,
            shortName: (f.place_name.split(",")[0] ?? "").trim(),
            coordinates: f.geometry.coordinates,
            bbox:
              Array.isArray(f.bbox) && f.bbox.length === 4
                ? (f.bbox as [number, number, number, number])
                : undefined,
          })
        );
      })
      .catch((err) => {
        if (err.name === "AbortError") {
          return null; // cancelled — ignore
        }
        return [] as PlaceSuggestion[];
      });

    const itemsPromise = fetch(
      `/api/search?q=${encodeURIComponent(effectiveQuery)}`,
      { signal }
    )
      .then((r) => r.json())
      .then((json) =>
        (json.results ?? []).map(
          (r: SearchApiResult): ItemSuggestion => ({
            kind: r.kind,
            id: r.id,
            name: r.name,
            city: r.city,
            subtitle: r.subtitle,
            logoUrl: r.logoUrl,
            coordinates: [r.longitude, r.latitude],
          })
        )
      )
      .catch((err) => {
        if (err.name === "AbortError") {
          return null; // cancelled — ignore
        }
        return [] as ItemSuggestion[];
      });

    Promise.all([placesPromise, itemsPromise]).then(([p, serverItems]) => {
      // null means the request was aborted — a new one is already in-flight
      if (p === null || serverItems === null) {
        return;
      }

      // Sort place results by distance from current map center (closest first)
      const mapCenter = getMapCenter();
      let sortedPlaces: PlaceSuggestion[] = p;
      if (mapCenter) {
        sortedPlaces = [...p].sort((a, b) => {
          const distA =
            (a.coordinates[0] - mapCenter[0]) ** 2 +
            (a.coordinates[1] - mapCenter[1]) ** 2;
          const distB =
            (b.coordinates[0] - mapCenter[0]) ** 2 +
            (b.coordinates[1] - mapCenter[1]) ** 2;
          return distA - distB;
        });

        // Suppress places that are all far from the map center when DB results exist.
        // ~10 degrees ≈ 1,000 km — if the user is searching for a specific group
        // by name, place results from a different continent are just noise.
        const FAR_THRESHOLD = 10 * 10; // squared degrees
        const allPlacesFar = sortedPlaces.every((s: PlaceSuggestion) => {
          const distSq =
            (s.coordinates[0] - mapCenter[0]) ** 2 +
            (s.coordinates[1] - mapCenter[1]) ** 2;
          return distSq > FAR_THRESHOLD;
        });
        if (allPlacesFar && serverItems.length > 0) {
          sortedPlaces = [];
        }
      }

      setApiPlaces(sortedPlaces);
      setApiItems(serverItems);
      setLoading(false);
      setActiveIndex(-1);
    });

    return () => {
      controller.abort();
    };
  }, [debouncedQuery, getMapCenter]);

  // Click-outside: close dropdown when focus leaves the search container
  const searchContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: PointerEvent) => {
      if (!searchContainerRef.current?.contains(e.target as Node)) {
        setApiPlaces([]);
        setApiItems([]);
        setLoading(false);
      }
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, []);

  const handleSelect = (suggestion: SearchSuggestion) => {
    setApiPlaces([]);
    setApiItems([]);
    setActiveIndex(-1);
    setLoading(false);
    onSuggestionSelect(suggestion);
    onSearchChange("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!dropdownOpen) {
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      // Wrap: going past the last item returns to -1 (no selection)
      setActiveIndex((i) => (i >= totalOptions - 1 ? -1 : i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      // Wrap: going above -1 jumps to the last item
      setActiveIndex((i) => (i <= -1 ? totalOptions - 1 : i - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      const all = [...apiPlaces, ...displayedItems];
      const selected = all[activeIndex];
      if (selected) {
        handleSelect(selected);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setApiPlaces([]);
      setApiItems([]);
      setActiveIndex(-1);
    } else if (e.key === "Tab") {
      // Let focus move naturally; just close the dropdown
      setApiPlaces([]);
      setApiItems([]);
      setActiveIndex(-1);
    }
  };

  return (
    <div className="absolute top-20 right-0 left-0 z-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3 md:pr-12">
          {/* Search */}
          <div
            className="relative w-full shrink-0 md:w-96"
            ref={searchContainerRef}
          >
            <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-activedescendant={
                activeIndex >= 0 ? `search-opt-${activeIndex}` : undefined
              }
              aria-autocomplete="list"
              aria-controls={dropdownOpen ? "search-listbox" : undefined}
              aria-expanded={dropdownOpen}
              aria-label="Search groups and programmes"
              autoComplete="off"
              className="rounded-full bg-background pr-9 pl-9 shadow-sm"
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search by name, city, country…"
              role="combobox"
              value={searchText}
            />
            {searchText && (
              <button
                aria-label="Clear search"
                className="absolute top-1/2 right-3 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => {
                  onSearchChange("");
                  setApiPlaces([]);
                  setApiItems([]);
                  setLoading(false);
                }}
                type="button"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            {dropdownOpen && (
              <SearchDropdown
                activeIndex={activeIndex}
                emptyResults={showEmpty}
                id="search-listbox"
                items={displayedItems}
                loading={showLoading}
                onSelect={handleSelect}
                places={apiPlaces}
                query={debouncedQuery}
              />
            )}
          </div>

          {/* Filter pills */}
          <div className="flex items-center gap-2 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {/* All pill */}
            <ToggleGroup
              className="flex gap-1"
              onValueChange={(v) => {
                if (v) {
                  onFiltersChange({
                    group_type: "",
                    programme_type: "",
                    type: "all",
                  });
                }
              }}
              spacing={2}
              type="single"
              value={filters.type === "all" ? "all" : ""}
              variant="outline"
            >
              <ToggleGroupItem className={PILL} value="all">
                All
              </ToggleGroupItem>
            </ToggleGroup>

            {/* Groups & Programmes dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className={DROPDOWN_PILL}
                data-state={typeActive ? "on" : "off"}
              >
                {typeTriggerLabel}
                <ChevronDown className="h-3 w-3 opacity-60" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-[min(80vw,20rem)]"
              >
                {groupTypeOptions.length > 0 && (
                  <DropdownMenuGroup>
                    <DropdownMenuLabel>Groups</DropdownMenuLabel>
                    {groupTypeOptions.map((o) => (
                      <DropdownMenuItem
                        key={o.value}
                        onClick={() =>
                          onFiltersChange({
                            group_type: o.value,
                            programme_type: "",
                            type: "group",
                          })
                        }
                      >
                        <ChairIcon className="size-5 shrink-0 text-muted-foreground" />
                        {o.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuGroup>
                )}

                {groupTypeOptions.length > 0 && programmeTypes.length > 0 && (
                  <DropdownMenuSeparator />
                )}

                {programmeTypes.length > 0 && (
                  <DropdownMenuGroup>
                    <DropdownMenuLabel>Programmes</DropdownMenuLabel>
                    {programmeTypes.map((p) => (
                      <DropdownMenuItem
                        key={p.code}
                        onClick={() =>
                          onFiltersChange({
                            group_type: "",
                            programme_type: p.code,
                            type: "programme",
                          })
                        }
                      >
                        {p.logoUrl ? (
                          // biome-ignore lint/performance/noImgElement: decorative inline logo; Next.js Image adds no meaningful benefit here
                          <img
                            alt={p.name}
                            className="h-5 w-5 shrink-0 object-contain"
                            height={20}
                            src={p.logoUrl}
                            width={20}
                          />
                        ) : (
                          <span className="h-5 w-5 shrink-0" />
                        )}
                        <div className="flex flex-col gap-0.5">
                          <span>{p.name}</span>
                          {PROGRAMME_TAGLINES[p.code] && (
                            <span className="font-normal text-muted-foreground text-xs">
                              {PROGRAMME_TAGLINES[p.code]}
                            </span>
                          )}
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuGroup>
                )}

                {(groupTypeOptions.length > 0 || programmeTypes.length > 0) && (
                  <DropdownMenuSeparator />
                )}

                <DropdownMenuItem asChild className="text-muted-foreground">
                  <Link href="/online">
                    Online groups
                    <ArrowUpRight className="ml-auto h-3.5 w-3.5" />
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Meeting Day */}
            <Select
              onValueChange={(v) =>
                onFiltersChange({ meeting_day: v === "__any__" ? "" : v })
              }
              value={filters.meeting_day || "__any__"}
            >
              <SelectTrigger className="w-36 shrink-0 bg-background">
                <SelectValue placeholder="Any day" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Any day</SelectItem>
                {filterOptions.meetingDays.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d.charAt(0).toUpperCase() + d.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
}
